'use client';

import { useI18n } from 'next-localization';

import { ChangeLocationModal } from '@/components/change-location';
import { DisclaimerModal } from '@/components/modals';
import { DISCLAIMER_MODAL_NAME } from '@/components/modals/disclaimer-modal/types';
import { useModalContext } from '@/components/modals/modal-context';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { CELEBRUS_BOOK_BUTTON_ID, CELEBRUS_BOOK_EXTERNAL_BUTTON_ID } from '@/lib/constants';
import { cn, getBookingPageUrl } from '@/lib/utils';
import { Divider } from '@xsf/shared-lib/components/divider';

import styles from '../profile.module.css';
import { AddressSectionProps } from '../profile.types';
import { createWebsiteUrl } from '../utils';
import { AppointmentButton } from './appointment-button';
import { CallFacilityButton } from './call-facility-button';

export const AddressSection = ({
  activeLocation,
  providerLocations,
  website,
  mhdParticipant,
  urlTitle,
  onlineSchedulingUrl,
  providerId,
}: AddressSectionProps): JSX.Element => {
  const { t } = useI18n();
  const { routerPush } = useCustomRouting();
  const { withBasePath } = useBasePath();
  const { closeModal, openModal } = useModalContext();

  const { activeProviderLocationByDoctorId } = useDistanceContext();
  const activeLocationId = activeProviderLocationByDoctorId[providerId];
  const location = providerLocations.find((loc) => loc.id === activeLocationId) || activeLocation;

  const showBookAppointmentSteps =
    !onlineSchedulingUrl && mhdParticipant && location.mhdCalendarId !== null;

  const handleConfirm = () => {
    window.open(onlineSchedulingUrl, '_blank');
    closeModal(DISCLAIMER_MODAL_NAME.HEALOW_BOOKING);
  };

  const handleCancel = () => {
    closeModal(DISCLAIMER_MODAL_NAME.HEALOW_BOOKING);
  };

  const openHealowModal = () => {
    openModal(DISCLAIMER_MODAL_NAME.HEALOW_BOOKING);
  };

  return (
    <>
      <DisclaimerModal
        onSuccess={handleConfirm}
        onDecline={handleCancel}
        titleTranslationKey="OAS.Profile.Details.BookAppointmentModalTitle"
        acceptButtonTranslationKey="OAS.Profile.Details.BookAppointmentModalConfirm"
        declineButtonTranslationKey="OAS.Profile.Details.BookAppointmentModalCancel"
        languageDropdownTranslationKey="OAS.Profile.Details.BookAppointmentModalLanguage"
        modalName={DISCLAIMER_MODAL_NAME.HEALOW_BOOKING}
      />
      <div className={cn('col-sm-12 col-lg-4 mt-2', styles.addressWrapper)}>
        {location && <Divider className="d-lg-none" />}

        <div>
          <p className={cn('d-flex justify-content-between fw-heavy mb-0', styles.addressTitle)}>
            <span>{t('OAS.Profile.Details.Address')}</span>
            {Number(location.distance) > 0 && !Number.isNaN(Number(location.distance)) && (
              <span>
                {location.distance} {t('OAS.ChangeLocation.Miles')}
              </span>
            )}
          </p>
          {website ? (
            <p>
              <a
                href={createWebsiteUrl(website)}
                target="_blank"
                rel="noreferrer"
                aria-label={t('OAS.Profile.Details.ClinicLinkText')}
              >
                {location.name}
              </a>
            </p>
          ) : (
            <p className={cn('fw-bold', styles.addressDisplayLocation)}>{location.name}</p>
          )}

          <p>
            {location.street}
            <br />
            {location.city}, {location.state} {location.zip}
          </p>
          {providerLocations?.length > 1 && (
            <ChangeLocationModal locations={providerLocations} providerId={providerId} icon="house">
              {t('OAS.Profile.Details.ChooseDifferentLocation')} ({providerLocations.length})
            </ChangeLocationModal>
          )}
        </div>

        {/* TODO: Refactor this double ternary for better readability and maintainability */}
        <div className="row justify-content-center">
          <div className={cn('col-xs-12 col-sm-6 col-lg-12', styles.addressButtonWrapper)}>
            {showBookAppointmentSteps ? (
              <AppointmentButton
                locationId={location.id}
                buttonText={t('OAS.Profile.Details.BookAppointment')}
                onClick={() => {
                  const url = withBasePath(getBookingPageUrl(urlTitle));
                  routerPush(url);
                }}
                appointmentButtonId={CELEBRUS_BOOK_BUTTON_ID}
              />
            ) : onlineSchedulingUrl ? (
              <AppointmentButton
                locationId={location.id}
                buttonText={t('OAS.Profile.Details.BookAppointment')}
                onClick={openHealowModal}
                appointmentButtonId={CELEBRUS_BOOK_EXTERNAL_BUTTON_ID}
              />
            ) : (
              <CallFacilityButton location={location} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
